/**
 * Which scripts a document contains, and which of them this PDF writer can render CORRECTLY.
 *
 * PDF STORES GLYPH IDS. There is no text engine inside a PDF: the producer decides which glyph
 * goes where, and the viewer draws them. So every transformation between characters and glyphs
 * — Arabic joining forms, Indic reordering and conjunct formation, Thai mark stacking,
 * bidirectional reordering — has to happen BEFORE the file is written, in the writer.
 *
 * This build does none of it, and that is a bounded, stated limitation rather than a bug:
 *
 *   • **LTR simple scripts** (Latin, Greek, Cyrillic, Han, Kana, Hangul, and the punctuation
 *     around them) need no shaping beyond a code-point → glyph lookup, which the `cmap` gives.
 *     These render correctly.
 *   • **Complex scripts** (Arabic, Hebrew, Devanagari, Bengali, Tamil, Thai, Lao, Khmer,
 *     Myanmar, Tibetan) do not. Arabic written without joining is a row of disconnected
 *     letters that a reader can see is wrong; Hebrew written without bidi comes out reversed,
 *     which a reader who does not know Hebrew CANNOT see is wrong — and shipping the second
 *     is worse than shipping neither.
 *
 * So the writer REFUSES a document containing one, with `WriteFailure { reason:
 * 'font-unavailable' }` and a detail naming the script and what is missing. The contract
 * carries no `shaping-unavailable` reason; `font-unavailable` is the failure the DoD names for
 * exactly this case ("an unshaped Arabic run yields font-unavailable rather than tofu or a
 * Latin substitution"), and the alternatives — tofu, or silently substituting a Latin face —
 * are both explicitly ruled out by `contracts/adapter.md` §6.
 *
 * The measured cost of doing it properly is in the research report: harfbuzzjs (168 KiB gzip
 * for `harfbuzz.wasm` plus 17 KiB of glue) and bidi-js (12 KiB gzip), on top of the PDF path.
 */

/** The script tags `contracts/adapter.md` §6 says the adapter scans for. */
export type ScriptTag =
  | 'latin'
  | 'greek'
  | 'cyrillic'
  | 'han'
  | 'kana'
  | 'hangul'
  | 'arabic'
  | 'hebrew'
  | 'devanagari'
  | 'thai'
  | 'emoji'
  | 'other';

interface Range {
  readonly from: number;
  readonly to: number;
  readonly script: ScriptTag;
}

/** Ordered so the first match wins; ranges do not overlap. */
const RANGES: readonly Range[] = [
  { from: 0x0000, to: 0x024f, script: 'latin' },
  { from: 0x0370, to: 0x03ff, script: 'greek' },
  { from: 0x0400, to: 0x052f, script: 'cyrillic' },
  { from: 0x0590, to: 0x05ff, script: 'hebrew' },
  { from: 0x0600, to: 0x06ff, script: 'arabic' },
  { from: 0x0750, to: 0x077f, script: 'arabic' },
  { from: 0x0870, to: 0x08ff, script: 'arabic' },
  { from: 0x0900, to: 0x097f, script: 'devanagari' },
  { from: 0x0980, to: 0x0dff, script: 'devanagari' }, // Bengali…Sinhala: same treatment
  { from: 0x0e00, to: 0x0e7f, script: 'thai' },
  { from: 0x0e80, to: 0x0fff, script: 'thai' }, // Lao, Tibetan
  { from: 0x1000, to: 0x109f, script: 'thai' }, // Myanmar
  { from: 0x1780, to: 0x17ff, script: 'thai' }, // Khmer
  { from: 0x1e00, to: 0x1eff, script: 'latin' },
  { from: 0x2000, to: 0x206f, script: 'latin' }, // general punctuation
  { from: 0x20a0, to: 0x20bf, script: 'latin' }, // currency
  { from: 0x2190, to: 0x2bff, script: 'other' }, // arrows, symbols, dingbats
  { from: 0x3000, to: 0x303f, script: 'han' }, // CJK punctuation
  { from: 0x3040, to: 0x30ff, script: 'kana' },
  { from: 0x3100, to: 0x312f, script: 'han' },
  { from: 0x3130, to: 0x318f, script: 'hangul' },
  { from: 0x31f0, to: 0x31ff, script: 'kana' },
  { from: 0x3400, to: 0x4dbf, script: 'han' },
  { from: 0x4e00, to: 0x9fff, script: 'han' },
  { from: 0xa960, to: 0xa97f, script: 'hangul' },
  { from: 0xac00, to: 0xd7ff, script: 'hangul' },
  { from: 0xf900, to: 0xfaff, script: 'han' },
  { from: 0xfb50, to: 0xfdff, script: 'arabic' },
  { from: 0xfe70, to: 0xfeff, script: 'arabic' },
  { from: 0xff00, to: 0xffef, script: 'han' }, // halfwidth/fullwidth forms
  { from: 0x1f300, to: 0x1faff, script: 'emoji' },
  { from: 0x20000, to: 0x3ffff, script: 'han' }, // CJK extensions B+
];

export function scriptOf(codePoint: number): ScriptTag {
  if (codePoint === 0x200d || (codePoint >= 0xfe00 && codePoint <= 0xfe0f)) return 'emoji';
  if (codePoint >= 0x1f1e6 && codePoint <= 0x1f1ff) return 'emoji';
  for (const range of RANGES) {
    if (codePoint >= range.from && codePoint <= range.to) return range.script;
  }
  return 'other';
}

/**
 * Scripts that need shaping, bidi, or both, and therefore cannot be written correctly here.
 * Keyed by script so the failure message can name what is actually missing.
 */
export const UNSHAPEABLE: Readonly<Partial<Record<ScriptTag, string>>> = {
  arabic:
    'Arabic needs contextual joining (initial/medial/final forms) and bidirectional reordering ' +
    'before glyph ids can be written. This build has neither, and a PDF written without them ' +
    'shows disconnected letters in the wrong order.',
  hebrew:
    'Hebrew needs bidirectional reordering before glyph ids can be written. Written without it ' +
    'the text comes out reversed — which, unlike a missing glyph, a reader who does not know ' +
    'the script cannot see is wrong.',
  devanagari:
    'Indic scripts need reordering and conjunct formation before glyph ids can be written. ' +
    'This build has neither.',
  thai:
    'Thai, Lao, Khmer and Myanmar need mark reordering and stacking before glyph ids can be ' +
    'written. This build has neither.',
};

export interface ScriptScan {
  readonly scripts: ReadonlySet<ScriptTag>;
  readonly codePoints: ReadonlySet<number>;
  /** Non-empty when the document contains something this writer cannot shape. */
  readonly blocking: readonly { readonly script: ScriptTag; readonly reason: string }[];
  /** True when every character is representable in WinAnsi — the standard-14 fallback gate. */
  readonly winAnsiOnly: boolean;
}

export function scanScripts(texts: Iterable<string>): ScriptScan {
  const scripts = new Set<ScriptTag>();
  const codePoints = new Set<number>();
  let winAnsiOnly = true;

  for (const text of texts) {
    for (const ch of text) {
      const cp = ch.codePointAt(0) as number;
      codePoints.add(cp);
      scripts.add(scriptOf(cp));
      if (winAnsiOnly && !isWinAnsi(cp)) winAnsiOnly = false;
    }
  }

  const blocking: { script: ScriptTag; reason: string }[] = [];
  for (const script of scripts) {
    const reason = UNSHAPEABLE[script];
    if (reason !== undefined) blocking.push({ script, reason });
  }
  return { scripts, codePoints, blocking, winAnsiOnly };
}

/**
 * WinAnsiEncoding coverage — what a non-embedded standard-14 font can actually show.
 *
 * It is Latin-1 minus the C1 control range, plus the handful of typographic characters Adobe
 * put in 0x80–0x9F (curly quotes, dashes, the euro). A character outside it would be written
 * as a byte the viewer draws as something else entirely, so the gate is exact rather than
 * approximate.
 */
const WINANSI_EXTRAS = new Set([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039, 0x0152,
  0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a,
  0x0153, 0x017e, 0x0178,
]);

export function isWinAnsi(cp: number): boolean {
  if (cp === 0x0a || cp === 0x0d || cp === 0x09) return true;
  if (cp >= 0x20 && cp <= 0x7e) return true;
  if (cp >= 0xa0 && cp <= 0xff) return true;
  return WINANSI_EXTRAS.has(cp);
}

/** Code point → WinAnsi byte. Only valid for a code point `isWinAnsi` accepted. */
export function winAnsiByte(cp: number): number {
  if (cp <= 0x7e || (cp >= 0xa0 && cp <= 0xff)) return cp;
  const index = [...WINANSI_EXTRAS].indexOf(cp);
  // The extras occupy 0x80–0x9F in the order they are listed above, with three holes Adobe
  // leaves undefined (0x81, 0x8D, 0x8F, 0x90, 0x9D). The table below is the exact mapping.
  const EXTRA_BYTES: Readonly<Record<number, number>> = {
    0x20ac: 0x80,
    0x201a: 0x82,
    0x0192: 0x83,
    0x201e: 0x84,
    0x2026: 0x85,
    0x2020: 0x86,
    0x2021: 0x87,
    0x02c6: 0x88,
    0x2030: 0x89,
    0x0160: 0x8a,
    0x2039: 0x8b,
    0x0152: 0x8c,
    0x017d: 0x8e,
    0x2018: 0x91,
    0x2019: 0x92,
    0x201c: 0x93,
    0x201d: 0x94,
    0x2022: 0x95,
    0x2013: 0x96,
    0x2014: 0x97,
    0x02dc: 0x98,
    0x2122: 0x99,
    0x0161: 0x9a,
    0x203a: 0x9b,
    0x0153: 0x9c,
    0x017e: 0x9e,
    0x0178: 0x9f,
  };
  void index;
  return EXTRA_BYTES[cp] ?? 0x3f; // '?'
}

/*───────────────────────────────────────────────────────────────────────────────────────
  The coverage verdict — CTO S16 §1, founder-ruled

  A PDF embeds glyphs, so a character the chosen font cannot draw is not a rendering nuance: it is
  a `.notdef` box, or nothing. The question is what to do about it, and the founder ruled on the
  case that matters:

    > Emojis are not really language specific. If there's an emoji of money or smiling, use the
    > emoji for it in any language. Don't transcribe it — use the same emoji.

  So an emoji must never cost a user their download. The rule that follows, and the reason it needs
  no tunable threshold:

    EVERY uncoverable character is a NON-LETTER  →  DEGRADE. Drop them, record the loss.
    ANY uncoverable character is a LETTER        →  REFUSE. The option should not be offered.

  Dropping a pictograph loses a decoration and leaves every word intact: `Hello world 👋` becomes
  `Hello world`, which is a fair trade the user can see reported. Dropping a diacritic mangles the
  word — `Zażółć` becomes `Zaó` — and folding it to `Zazolc` changes the spelling, which is a
  different feature the user would have to ask for. Nobody has to defend "12% is fine and 15% is
  not" in six months.

  MEASURED against every case, chars outside WinAnsi / of which letters:
    emoji 1/13 (0 letters) · ZWJ family 7/13 (0) · arrows and box-drawing 4/9 (0)   → DEGRADE
    Vietnamese 2/19 (2) · Turkish 4/15 (4) · Polish 8/17 (8) · Russian 9/10 (9)     → REFUSE
    Chinese, Japanese, Korean 100% · Thai, Hebrew, Arabic 100% (unshapeable)        → REFUSE
───────────────────────────────────────────────────────────────────────────────────────*/

/** Unicode letters. A regex property escape, not `Intl` — the boundary rules ban the latter. */
const LETTER = /\p{L}/u;

export interface CoverageVerdict {
  /**
   * 'writable' — every character has a glyph.
   * 'degrade'  — only non-letters are uncoverable; drop them and report.
   * 'refuse'   — a letter or an unshapeable script is uncoverable; do not offer the format.
   */
  readonly kind: 'writable' | 'degrade' | 'refuse';
  /** Uncoverable code points, deduped, in scan order. */
  readonly uncoverable: readonly number[];
  /** The subset that are letters. Non-empty is exactly what makes the verdict 'refuse'. */
  readonly letters: readonly number[];
  /** Scripts no amount of font support fixes here — shaping and bidi are not implemented. */
  readonly unshapeable: readonly { readonly script: ScriptTag; readonly reason: string }[];
}

/**
 * `hasEmbeddedFont` is the caller's answer to `ctx.resources.font(...)`, which is why the writer's
 * pre-check must take `ctx`: the SAME document is writable or not depending on whether a font
 * arrived. The day `sendFonts` delivers a face with emoji coverage, this returns 'writable' for a
 * document it refuses today, with no change here.
 *
 * The embedded path is deliberately not second-guessed: coverage of an embedded face is the
 * subsetter's business, and duplicating that judgement here is how two checks start to disagree.
 *
 * `covers` names what the viewer's own fonts draw besides WinAnsi: the writer passes the standard
 * CJK fonts (cjk.ts `isStandardCjk`) when no face arrived, so a Chinese, Japanese or Korean
 * document is writable with no font in the file, and an emoji beside it degrades as it would
 * beside English.
 */
export function coverageVerdict(
  scan: ScriptScan,
  hasEmbeddedFont: boolean,
  covers: (cp: number) => boolean = () => false,
): CoverageVerdict {
  const unshapeable = scan.blocking;
  // Shaping and bidi are not implemented, so no font rescues these — the verdict is refuse either way.
  if (unshapeable.length > 0) {
    return { kind: 'refuse', uncoverable: [], letters: [], unshapeable };
  }
  if (hasEmbeddedFont) return { kind: 'writable', uncoverable: [], letters: [], unshapeable };

  const uncoverable: number[] = [];
  const letters: number[] = [];
  for (const cp of scan.codePoints) {
    if (isWinAnsi(cp) || covers(cp)) continue;
    uncoverable.push(cp);
    if (LETTER.test(String.fromCodePoint(cp))) letters.push(cp);
  }
  if (uncoverable.length === 0) {
    return { kind: 'writable', uncoverable, letters, unshapeable };
  }
  return { kind: letters.length > 0 ? 'refuse' : 'degrade', uncoverable, letters, unshapeable };
}

/**
 * Removes the given code points from a string. Used only on a 'degrade' verdict, so only ever
 * non-letters — every word survives intact, which is the whole basis of the rule.
 */
export function stripCodePoints(text: string, drop: ReadonlySet<number>): string {
  if (drop.size === 0) return text;
  let out = '';
  for (const ch of text) {
    if (!drop.has(ch.codePointAt(0) as number)) out += ch;
  }
  return out;
}
